import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAccountStore } from '~/stores/account'
import { usePickemApi } from '~/services/api'
import type { Stage, StageUnlock } from '../types'

// Хранилище условий разблокировки этапов.
//
// Зачем отдельный стор:
//   - При первой загрузке `Stage` уже содержит поле `unlock` (см.
//     03-api-basic.md, Shape: Stage), поэтому первичный рендер не требует
//     отдельного запроса.
//   - Но условия меняются «вне нашего домена» — пользователь, например,
//     пополняет счёт Лиги Ставок и возвращается на вкладку. Полный
//     `GET /stages/{id}` тяжёлый (там вся swiss/bracket внутри). Поэтому
//     для рефреша используем лёгкий `/stages/{id}/unlock-conditions` —
//     эндпоинт-«дубль» сделан как раз для этого (см. документ).
//
// Логика:
//   - `setLocal(stageId, unlock)` — при первом рендере мы засовываем сюда
//     unlock-snapshot из полного Stage. Это позволяет UI читать условия из
//     одного места.
//   - `refresh(stageId)` — лёгкий запрос; при success обновляем snapshot и
//     уведомляем подписчиков (например, StageView перерисуется и при
//     `user_unlocked === true` покажет нормальный SwissBoard вместо
//     UnlockBanner).
//   - `subscribeToFocus(stageId)` — добавляет слушатели visibilitychange/focus
//     и каждый раз при возврате на вкладку дёргает refresh. Это закрывает
//     основной кейс «выполнил условие во внешней системе → вернулся».

export const useUnlockStore = defineStore('pickem-stage-unlock', () => {
  const pickemApi = usePickemApi()
  const accountStore = useAccountStore()

  const map = ref<Map<Stage['id'], StageUnlock>>(new Map())
  const inFlight = ref<Set<Stage['id']>>(new Set())

  const setLocal = (id: Stage['id'], unlock: StageUnlock) => {
    const next = new Map(map.value)
    next.set(id, unlock)
    map.value = next
  }

  const get = (id: Stage['id']): StageUnlock | null => map.value.get(id) ?? null

  const refresh = async (id: Stage['id']): Promise<StageUnlock | null> => {
    if (inFlight.value.has(id)) return get(id)
    const next = new Set(inFlight.value)
    next.add(id)
    inFlight.value = next

    try {
      if (!accountStore.isAuthorized) {
        return get(id)
      }

      const unlock = await pickemApi.getStageUnlockConditions(id)
      setLocal(id, unlock)
      return unlock
    } finally {
      const after = new Set(inFlight.value)
      after.delete(id)
      inFlight.value = after
    }
  }

  // Возвращает функцию-отписку. Используем onMounted/onBeforeUnmount в
  // компонентах: при клике по action_url юзер чаще всего уходит на сторонний
  // ресурс и возвращается обратно по visibility/focus. Также рефрешим при
  // `pageshow` (восстановление из bfcache).
  const subscribeToFocus = (id: Stage['id']): (() => void) => {
    if (typeof window === 'undefined') return () => undefined

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh(id).catch(() => undefined)
      }
    }
    const onFocus = () => refresh(id).catch(() => undefined)
    const onPageShow = () => refresh(id).catch(() => undefined)

    window.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)
    window.addEventListener('pageshow', onPageShow)

    return () => {
      window.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('pageshow', onPageShow)
    }
  }

  return {
    map,
    inFlight,
    get,
    setLocal,
    refresh,
    subscribeToFocus
  }
})
